import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import {
  attachBillDeposit,
  cancelDeposit,
  createDeposit,
  detachBillDeposit,
  getAvailableDeposits,
  getDepositsReport,
  issueDepositReceipt,
  type DepositCreateIn,
  type DepositsReportFilters,
} from "../api/deposits";

/** Ключът на активните депозити — опреснява се след вземане и след закриване. */
export const AVAILABLE_DEPOSITS_KEY = ["deposits", "available"] as const;

/** Депозитите за „Използвай депозит" — само докато изборът е отворен. */
export const useAvailableDeposits = (enabled: boolean) =>
  useQuery({
    queryKey: AVAILABLE_DEPOSITS_KEY,
    queryFn: getAvailableDeposits,
    enabled,
    staleTime: 0,
  });

/** Списъкът в „Закрий" и „Справка депозити" — след всяко вземане и бон. */
const invalidateDeposits = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: ["deposits"] });
  queryClient.invalidateQueries({ queryKey: ["reports", "deposits"] });
};

// И при грешка: без отговор (или 5xx) депозитът може вече да е записан, а
// бонът — на хартия; списъците показват каквото е в базата.
export const useCreateDeposit = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: DepositCreateIn) => createDeposit(data),
    onSettled: () => invalidateDeposits(queryClient),
  });
};

/** „Издай бон" (`POST /deposits/{id}/receipt`) — бюджетът на чакането по
 *  метода на депозита (`api/deposits.ts::issueDepositReceipt`). */
export const useIssueDepositReceipt = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ depositId, paymentMethodId }: { depositId: number; paymentMethodId?: number }) =>
      issueDepositReceipt(depositId, paymentMethodId),
    onSettled: () => invalidateDeposits(queryClient),
  });
};

/** „Анулирай" (`POST /deposits/{id}/cancel`) — списъците и при грешка
 *  (отказът може да е, защото друг вече го е анулирал или закачил). */
export const useCancelDeposit = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ depositId, reason }: { depositId: number; reason: string }) =>
      cancelDeposit(depositId, reason),
    onSettled: () => invalidateDeposits(queryClient),
  });
};

/** Сметката и депозитите — след закачане/махане, и при грешка (отговорът
 *  може да се е загубил след записа). */
const invalidateBillDeposit = (queryClient: QueryClient) => {
  invalidateDeposits(queryClient);
  queryClient.invalidateQueries({ queryKey: ["bills"] });
};

/** „Използвай депозит" (`PUT /bills/{id}/deposit`). */
export const useAttachBillDeposit = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ billId, depositId }: { billId: number; depositId: number }) =>
      attachBillDeposit(billId, depositId),
    onSettled: () => invalidateBillDeposit(queryClient),
  });
};

/** „Махни депозита" (`DELETE /bills/{id}/deposit`). */
export const useDetachBillDeposit = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (billId: number) => detachBillDeposit(billId),
    onSettled: () => invalidateBillDeposit(queryClient),
  });
};

export const useDepositsReport = (filters: DepositsReportFilters) =>
  useQuery({
    queryKey: ["reports", "deposits", filters],
    queryFn: () => getDepositsReport(filters),
    enabled: !!filters.start_date && !!filters.end_date,
    staleTime: 0,
  });
